import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Info, Pencil, Check, X, Plus, AlertTriangle, Undo2, Phone, Mail } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Card } from "@/components/ui";

// =============================================================================
// About this job — mig 507 (4 Nov 2026)
//
// James, setting the record: "a SITE has an asset owner. The PROJECT has a
// carrier if it's an active upgrade, or the asset owner if it's structural.
// NBN: owner and carrier are one." And: "client contacts — I want to start
// assigning PEs on the client side to projects."
//
// One card, top of Overview: client · carrier · program · work type · the
// client's PE. Managers edit in place. "Carrier not set" is amber because
// three screens read it (hours report, PMO board, projects chips). A carrier
// the 507 backfill guessed shows its reason and an undo.
// =============================================================================

type Contact = { contact_id: string; full_name: string; role_title: string | null; email: string; phone: string | null; is_active: boolean };
type Guess = { backfill_id: string; field: string; reason: string; applied_at: string };

export default function ProjectAboutCard({ project, canManage, onChanged }: { project: any; canManage: boolean; onChanged: () => void }) {
  const qc = useQueryClient();
  const [edit, setEdit] = useState(false);
  const [carrier, setCarrier] = useState<string>(project.carrier_id ?? "");
  const [program, setProgram] = useState<string>(project.program ?? "");
  const [workType, setWorkType] = useState<string>(project.project_type ?? "");
  const [pe, setPe] = useState<string>(project.client_pe_contact_id ?? "");
  const [addingPe, setAddingPe] = useState(false);
  const [newPe, setNewPe] = useState({ full_name: "", email: "", phone: "", role_title: "Project Engineer" });

  const { data: carriers = [] } = useQuery({ queryKey: ["carriers"], queryFn: async () => (await supabase.from("carriers").select("carrier_id, carrier_code, legal_name").eq("is_active", true).order("carrier_code")).data ?? [] });
  const { data: contacts = [] } = useQuery({
    queryKey: ["client_contacts", project.client_id], enabled: !!project.client_id,
    queryFn: async () => { const { data } = await (supabase as any).from("client_contacts").select("contact_id, full_name, role_title, email, phone, is_active").eq("client_id", project.client_id).eq("is_active", true).order("full_name"); return (data ?? []) as Contact[]; },
  });
  const { data: programs = [] } = useQuery({
    queryKey: ["programs", project.client_id], enabled: !!project.client_id,
    queryFn: async () => { const { data } = await (supabase as any).from("projects").select("program").eq("client_id", project.client_id).not("program", "is", null); return [...new Set(((data ?? []) as { program: string }[]).map(r => r.program).filter(Boolean))].sort(); },
  });
  const { data: workTypes = [] } = useQuery({
    queryKey: ["work_types"],
    queryFn: async () => { const { data } = await (supabase as any).from("projects").select("project_type").not("project_type", "is", null); return [...new Set(((data ?? []) as { project_type: string }[]).map(r => r.project_type).filter(Boolean))].sort(); },
  });
  const { data: guesses = [] } = useQuery({
    queryKey: ["project_backfill", project.project_id],
    queryFn: async () => { const { data } = await (supabase as any).from("project_field_backfill").select("backfill_id, field, reason, applied_at").eq("project_id", project.project_id).is("undone_at", null); return (data ?? []) as Guess[]; },
  });

  const save = useMutation({
    mutationFn: async () => {
      const { error } = await (supabase as any).from("projects").update({
        carrier_id: carrier || null, program: program.trim() || null, project_type: workType.trim() || null, client_pe_contact_id: pe || null,
      }).eq("project_id", project.project_id);
      if (error) throw error;
    },
    onSuccess: () => { setEdit(false); qc.invalidateQueries({ queryKey: ["project", project.project_id] }); qc.invalidateQueries({ queryKey: ["programs"] }); onChanged(); },
    onError: (e: any) => window.alert(`Couldn't save: ${e?.message ?? e}`),
  });
  const addPe = useMutation({
    mutationFn: async () => {
      if (!newPe.full_name.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(newPe.email.trim())) throw new Error("A name and a valid email are needed");
      const { data, error } = await (supabase as any).from("client_contacts").insert({
        client_id: project.client_id, full_name: newPe.full_name.trim(), email: newPe.email.trim().toLowerCase(), phone: newPe.phone.trim() || null, role_title: newPe.role_title.trim() || null, is_active: true,
      }).select("contact_id").single();
      if (error) throw error;
      return data.contact_id as string;
    },
    onSuccess: (id) => { setPe(id); setAddingPe(false); setNewPe({ full_name: "", email: "", phone: "", role_title: "Project Engineer" }); qc.invalidateQueries({ queryKey: ["client_contacts", project.client_id] }); },
    onError: (e: any) => window.alert(`Couldn't add the contact: ${e?.message?.includes("duplicate") || e?.message?.includes("unique") ? "a contact with that email already exists for this client (it may be inactive — see the client's page)" : e?.message ?? e}`),
  });
  const undo = useMutation({
    mutationFn: async (id: string) => { const { error } = await (supabase.schema("app") as any).rpc("project_backfill_undo", { p_backfill_id: id }); if (error) throw error; },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["project", project.project_id] }); qc.invalidateQueries({ queryKey: ["project_backfill", project.project_id] }); onChanged(); },
    onError: (e: any) => window.alert(`Couldn't undo: ${e?.message ?? e}`),
  });

  const peContact = contacts.find(c => c.contact_id === (project.client_pe_contact_id ?? "")) ?? null;
  const carrierGuess = guesses.find(g => g.field === "carrier_id") ?? null;
  const owner = project.sites?.asset_owner ?? null;
  const inp = "bg-ink border border-line rounded px-2 py-1.5 text-[13px] text-text w-full";

  return (
    <Card>
      <div className="p-5">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[12px] font-medium text-text-muted uppercase tracking-wider flex items-center gap-1.5"><Info size={13}/>About this job</div>
          {canManage && !edit && <button type="button" onClick={() => { setCarrier(project.carrier_id ?? ""); setProgram(project.program ?? ""); setWorkType(project.project_type ?? ""); setPe(project.client_pe_contact_id ?? ""); setEdit(true); }} className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] text-text-muted hover:text-accent hover:bg-ink-hover"><Pencil size={11}/> Edit</button>}
          {edit && (
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => save.mutate()} disabled={save.isPending} className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[11px] bg-accent text-white"><Check size={11}/> Save</button>
              <button type="button" onClick={() => { setEdit(false); setAddingPe(false); }} className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] text-text-muted hover:text-text"><X size={11}/></button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-3">
          <Field label="Client"><Link to={`/clients`} className="text-text hover:text-accent">{project.clients?.legal_name ?? "—"}</Link></Field>

          <Field label="Carrier" warn={!edit && !project.carrier_id}>
            {edit ? (
              <select value={carrier} onChange={e => setCarrier(e.target.value)} className={inp}>
                <option value="">— not set —</option>
                {(carriers as any[]).map(c => <option key={c.carrier_id} value={c.carrier_id}>{c.carrier_code} · {c.legal_name}</option>)}
              </select>
            ) : project.carriers?.carrier_code ? (
              <>
                <span className="font-mono">{project.carriers.carrier_code}</span>
                {carrierGuess && <div className="text-[10.5px] text-text-dim mt-0.5 leading-snug">guessed: {carrierGuess.reason}{canManage && <button type="button" onClick={() => { if (window.confirm("Clear the guessed carrier? It won't be guessed again — pick it by hand.")) undo.mutate(carrierGuess.backfill_id); }} className="ml-1 text-accent inline-flex items-center gap-0.5"><Undo2 size={10}/>undo</button>}</div>}
              </>
            ) : (
              <span className="text-warn inline-flex items-center gap-1 text-[12px]"><AlertTriangle size={11}/>not set{owner?.owner_code ? ` · tower owner ${owner.owner_code}` : ""}</span>
            )}
          </Field>

          <Field label="Program">
            {edit ? (<><input list="about-programs" value={program} onChange={e => setProgram(e.target.value)} placeholder="e.g. Telstra Macro" className={inp}/><datalist id="about-programs">{programs.map(p => <option key={p} value={p}/>)}</datalist></>)
                  : (project.program ?? <span className="text-text-dim">—</span>)}
          </Field>

          <Field label="Work type">
            {edit ? (<><input list="about-worktypes" value={workType} onChange={e => setWorkType(e.target.value)} placeholder="e.g. Macro upgrade, Structural, Civils" className={inp}/><datalist id="about-worktypes">{workTypes.map(p => <option key={p} value={p}/>)}</datalist></>)
                  : (project.project_type ?? <span className="text-text-dim">—</span>)}
          </Field>

          <Field label="Client PE">
            {edit ? (
              <div className="space-y-1.5">
                <select value={pe} onChange={e => { if (e.target.value === "__new") { setAddingPe(true); } else { setPe(e.target.value); setAddingPe(false); } }} className={inp}>
                  <option value="">— not assigned —</option>
                  {contacts.map(c => <option key={c.contact_id} value={c.contact_id}>{c.full_name}{c.role_title ? ` · ${c.role_title}` : ""}</option>)}
                  <option value="__new">+ add a contact for {project.clients?.legal_name ?? "this client"}…</option>
                </select>
                {addingPe && (
                  <div className="rounded border border-line bg-ink-raised p-2 space-y-1.5">
                    <input value={newPe.full_name} onChange={e => setNewPe({ ...newPe, full_name: e.target.value })} placeholder="Name" className={inp}/>
                    <input value={newPe.email} onChange={e => setNewPe({ ...newPe, email: e.target.value })} placeholder="Email" className={inp}/>
                    <input value={newPe.phone} onChange={e => setNewPe({ ...newPe, phone: e.target.value })} placeholder="Phone" className={inp}/>
                    <input value={newPe.role_title} onChange={e => setNewPe({ ...newPe, role_title: e.target.value })} placeholder="Role" className={inp}/>
                    <div className="flex gap-1"><button type="button" onClick={() => addPe.mutate()} disabled={addPe.isPending} className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] bg-accent text-white"><Plus size={11}/>Add & pick</button><button type="button" onClick={() => setAddingPe(false)} className="px-2 py-1 rounded text-[11px] text-text-muted">cancel</button></div>
                  </div>
                )}
              </div>
            ) : peContact ? (
              <div>
                <div>{peContact.full_name}{peContact.role_title && <span className="text-text-muted text-[11px]"> · {peContact.role_title}</span>}</div>
                <div className="text-[11px] text-text-muted flex flex-wrap gap-x-3 mt-0.5">
                  {peContact.phone && <a href={`tel:${peContact.phone}`} className="inline-flex items-center gap-1 hover:text-accent"><Phone size={10}/>{peContact.phone}</a>}
                  <a href={`mailto:${peContact.email}`} className="inline-flex items-center gap-1 hover:text-accent"><Mail size={10}/>{peContact.email}</a>
                </div>
              </div>
            ) : <span className="text-text-dim">{canManage ? "not assigned — Edit to pick" : "—"}</span>}
          </Field>
        </div>

        {owner?.owner_code && (
          <div className="mt-3 pt-3 border-t border-line text-[10.5px] text-text-dim leading-snug">
            The site's owner is <span className="font-mono">{owner.owner_code}</span>{owner.display_name ? ` (${owner.display_name})` : ""}. The job's carrier is who the work is for — the owner when it's structural, the carrier when it's their upgrade; NBN is both.
          </div>
        )}
      </div>
    </Card>
  );
}

function Field({ label, warn, children }: { label: string; warn?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <div className={`text-[11px] uppercase tracking-wider font-medium ${warn ? "text-warn" : "text-text-muted"}`}>{label}</div>
      <div className="text-[13px] text-text mt-1">{children}</div>
    </div>
  );
}
